import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@asigna/kit';
import { makeStyles } from '@material-ui/core';
import { signatureVrsToRsv } from '@stacks/common';
import { AddressVersion, createStacksPublicKey, publicKeyToAddress } from '@stacks/transactions';
import { LedgerError } from '@zondax/ledger-stacks';
import { login } from 'api';
import { InputField } from 'components/InputField/InputField';
import { CONNECT_MESSAGE } from 'constants/base';
import { CommonLedgerDeviceInlineWarnings } from 'hooks/ledger/ledger-inline-warnings';
import { pullStacksKeysFromLedgerDevice } from 'hooks/ledger/request-stacks-keys.utils';
import LedgerLogo from 'images/ledger_logo.png';
import { useAtom } from 'jotai';

import {
  connectLedgerStacksApp,
  getStacksAppVersion,
  isStacksLedgerAppClosed,
  signLedgerUtf8Message,
} from '../../../hooks/ledger/stacks-ledger-utils';
import { useRequestLedgerKeys } from '../../../hooks/ledger/use-request-ledger-keys';
import useNetwork from '../../../hooks/use-network';
import { PROVIDERS } from '../../../hooks/useData';
import { addressAtom } from '../../../store/address';
import { getLedgerStorage } from '../../../store/ledger/ledger-storage';
import { StxChainEnum } from '../../../types/enums';
import { getStorageKeysForNetwork } from '../../../utils/LocalstorageUtils';
import { Dropdown } from '../../Dropdown';
import { TransactionCommonDialog } from '../TransactionDialog/TransactionCommonDialog';

interface TransactionCommonProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(() => ({
  error: {
    color: '#D92B2B',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
  },
  loader: {
    width: 50,
    height: 50,
    margin: '50px auto',
  },
  connectedTxt: {
    color: '#FAFAFA',
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '32px',
  },
  advancedTxt: {
    color: '#ABABAB',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '22px',
  },
}));

interface IAddressSelection {
  path: string;
  stxPublicKey: string;
  index: number;
  address: string;
}

const validateDerivation = (derivation: string) => {
  const regexp = new RegExp("^(m/)?([0-9]+'?/)*[0-9]+'?$");
  if (!derivation) return false;

  return derivation.match(regexp);
};

export const LedgerConnectDialog = (props: TransactionCommonProps) => {
  const { open, onClose } = props;
  const [hasError, setHasError] = useState(false);
  const [addresses, setAddresses] = useState<IAddressSelection[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<IAddressSelection>();
  const [network] = useNetwork();
  const [, setAddress] = useAtom(addressAtom);
  const [signMessageResponse, setSignMessageResponse] = useState<any>();
  const [awaitingSignature, setAwaitingSignature] = useState(false);
  const [customPath, setCustomPath] = useState('');
  const derivationPathOptions = ["m/44'/5757'/0'/0", "m/44'/0'/0'/0/0", 'Custom'];
  const [derivationPath, setDerivationPath] = useState(derivationPathOptions[0]);
  const customPathRef = useRef(customPath);
  const derivationPathRef = useRef(derivationPath);

  const { requestKeys, latestDeviceResponse, awaitingDeviceConnection, outdatedAppVersionWarning } =
    useRequestLedgerKeys({
      chain: 'stacks',
      connectApp: connectLedgerStacksApp,
      getAppVersion: getStacksAppVersion,
      isAppOpen(resp) {
        return !isStacksLedgerAppClosed(resp);
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSuccess() {},
      async pullKeysFromDevice(app) {
        const customPath =
          derivationPathRef.current === 'Custom'
            ? customPathRef.current || ''
            : derivationPathRef.current;
        const resp = await pullStacksKeysFromLedgerDevice(
          app,
          customPath,
          network
        )({
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onRequestKey(accountIndex) {},
        });
        if (resp.status === 'failure') {
          setHasError(true);

          return;
        }
        const addr = resp.publicKeys.map((r: any, index: number) => ({
          path: r.path,
          stxPublicKey: r.stxPublicKey,
          index: index,
          address: publicKeyToAddress(
            network === 'mainnet'
              ? AddressVersion.MainnetSingleSig
              : AddressVersion.TestnetSingleSig,
            createStacksPublicKey(r.stxPublicKey)
          ),
        }));
        setAddresses(addr);
        setSelectedAddress(addr[0]);
      },
    });

  const handleConnect = async () => {
    try {
      setHasError(false);
      await requestKeys();
    } catch (error) {
      setHasError(true);
      console.error('An error occurred:', error);
    }
  };

  const handleSelectAddress = async () => {
    let stacksApp: Awaited<ReturnType<typeof connectLedgerStacksApp>> | null = null;
    try {
      stacksApp = await connectLedgerStacksApp();
      console.log('stacksApp1', stacksApp);
      const versionInfo = await getStacksAppVersion(stacksApp);
      console.log('versionInfo', versionInfo);
      setSignMessageResponse(versionInfo);
      setAwaitingSignature(true);

      if (versionInfo.deviceLocked) {
        throw new Error('Device locked');
      }

      const index = addresses.findIndex((a) => a.address === selectedAddress?.address) || 0;

      const customPath =
        derivationPathRef.current === 'Custom'
          ? customPathRef.current || ''
          : derivationPathRef.current;

      console.log('customPath', customPath);
      const resp = await signLedgerUtf8Message(stacksApp)(
        CONNECT_MESSAGE,
        `${customPath}/${index}`
      );
      console.log('resp', resp);
      // Assuming here that public keys are wrong. Alternatively, we may want
      // to proactively check the key before signing
      if (resp.returnCode === LedgerError.DataIsInvalid) {
        throw new Error(LedgerError.DataIsInvalid.toString());
      }

      if (resp.returnCode === LedgerError.TransactionRejected) {
        throw new Error(LedgerError.TransactionRejected.toString());
      }
      if (resp.returnCode !== LedgerError.NoErrors) {
        throw new Error('Some other error');
      }

      const signature = signatureVrsToRsv(resp.signatureVRS.toString('hex'));
      const ledgerStorage = getLedgerStorage();
      ledgerStorage.setLedgerInfo({
        publicKey: selectedAddress?.stxPublicKey || '',
        index,
      });
      await login({
        network: StxChainEnum[network],
        publicKey: selectedAddress?.stxPublicKey || '',
        signature: signature,
        address: selectedAddress?.address || '',
        setAddress,
      });
      onClose();
      localStorage.setItem(getStorageKeysForNetwork().walletType, PROVIDERS.Ledger);
      localStorage.setItem(getStorageKeysForNetwork().derivationPath, `${customPath}/${index}`);
    } catch (e) {
      console.error('An error LEDGER occurred:', e);
      setAwaitingSignature(false);
    } finally {
      console.log('stacksApp close connection', stacksApp);
      if (stacksApp) {
        try {
          await stacksApp.transport.close();
        } catch {
          // ignore close errors
        }
      }
    }
  };

  const classes = useStyles();
  const addressesLoaded = addresses.length > 0;

  useEffect(() => {
    customPathRef.current = customPath;
  }, [customPath]);

  useEffect(() => {
    derivationPathRef.current = derivationPath;
  }, [derivationPath]);

  const isValidCustomPath = validateDerivation(customPath);

  return (
    <TransactionCommonDialog
      open={open}
      isOverflowVisible
      title={addressesLoaded ? 'Select account' : 'Connect ledger'}
      button={
        <Button
          fullWidth
          onClick={() => (addressesLoaded ? handleSelectAddress() : handleConnect())}
          disabled={
            awaitingDeviceConnection ||
            awaitingSignature ||
            (derivationPath === 'Custom' && !isValidCustomPath)
          }
          message={addressesLoaded ? 'Confirm selection' : 'Connect'}
          colorType="primary"
        />
      }
      onClose={onClose}
    >
      <div className="flex flex-col w-full my-24 gap-16">
        {awaitingSignature && <>Please sign message to authorize</>}
        {awaitingDeviceConnection || awaitingSignature ? (
          <img className={classes.loader} src="/loading-gif.gif" alt="" />
        ) : (
          <>
            <CommonLedgerDeviceInlineWarnings
              latestDeviceResponse={addressesLoaded ? signMessageResponse : latestDeviceResponse}
              chain="Stacks"
              outdatedLedgerAppWarning={outdatedAppVersionWarning}
            />
            <img src={LedgerLogo} alt="" className="mx-auto w-40 h-40" />
            {!addressesLoaded && (
              <div className={classes.connectedTxt}>
                Make sure your Ledger is connected and the Stacks app is opened.
              </div>
            )}

            {!addressesLoaded && (
              <div className={classes.advancedTxt}>
                Select or enter the base derivation path without the last address index.
              </div>
            )}
            {!addressesLoaded && (
              <Dropdown
                items={derivationPathOptions}
                item={derivationPath}
                onChange={(path) => setDerivationPath(path)}
              />
            )}
            {!addressesLoaded && derivationPath === 'Custom' && (
              <InputField
                value={customPath}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomPath(val);
                }}
              />
            )}
            {addressesLoaded && (
              <Dropdown
                items={addresses.map((a) => a.address)}
                item={selectedAddress?.address}
                onChange={(addr) => setSelectedAddress(addresses.find((a) => a.address === addr)!)}
              />
            )}

            {hasError && <span className={classes.error}>Error signing transaction</span>}
          </>
        )}
      </div>
    </TransactionCommonDialog>
  );
};
